import type { Member } from '@sendbird/chat/groupChannel';
import { useChannelSettingsContext } from '@sendbird/uikit-react/ChannelSettings/context';
import useLocalization from '@sendbird/uikit-react/hooks/useLocalization';
import UserListItem, {
  type UserListItemProps,
} from '@sendbird/uikit-react/ui/UserListItem';
import UserListItemMenu from '@sendbird/uikit-react/ui/UserListItemMenu';
import {
  type FC,
  type ReactElement,
  type ReactNode,
  type SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslate } from 'ra-core';
import { useMuteUser } from '../../../api/hooks/sendbird/useMuteUser';
import { Button } from '../../../components/ui/button';
import { ChannelSettingsMenuItem } from './ChannelSettingsMenuItem';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';

interface MutedMembersListProps {
  renderUserListItem?: (
    props: UserListItemProps & { index: number }
  ) => ReactNode;
}

interface MutedMembersModalProps {
  onCancel: () => void;
  renderUserListItem: FC<UserListItemProps & { index: number }>;
}

const MutedMembersModal = ({
  onCancel,
  renderUserListItem: RenderUserListItem,
}: MutedMembersModalProps): ReactElement => {
  const [members, setMembers] = useState<Array<Member>>([]);
  const memberQueryRef = useRef<ReturnType<
    typeof channel.createMemberListQuery
  > | null>(null);
  const { channel } = useChannelSettingsContext();
  const { stringSet } = useLocalization();
  const translate = useTranslate();
  const { mutate: patchMuteUser, isPending: isMutePending } = useMuteUser();

  const unmuteAndRemove = (userId: string) => {
    patchMuteUser(
      {
        channelUrl: channel.url,
        userId,
        muted: false,
      },
      {
        onSuccess: () => {
          setMembers((prevMembers) =>
            prevMembers.filter((member) => member.userId !== userId)
          );
        },
      }
    );
  };

  useEffect(() => {
    const createdQuery = channel.createMemberListQuery({
      limit: 20,
      mutedMemberFilter: 'muted',
    } as any);

    void createdQuery.next().then((nextMembers) => {
      setMembers(nextMembers);
    });

    memberQueryRef.current = createdQuery;
  }, [channel]);

  const fetchMore = useCallback(() => {
    const memberQuery = memberQueryRef.current;

    if (!memberQuery?.hasNext) return;

    void memberQuery.next().then((nextMembers) => {
      setMembers((prevMembers) => [...prevMembers, ...nextMembers]);
    });
  }, []);

  const onScroll = (event: SyntheticEvent<HTMLDivElement>) => {
    const target = event.currentTarget;
    if (target.scrollTop + target.clientHeight >= target.scrollHeight) {
      fetchMore();
    }
  };

  return (
    <Dialog
      open
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onCancel();
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-hidden p-0 sm:max-w-lg">
        <DialogHeader className="px-6 pt-6 pb-4">
          <DialogTitle>
            {stringSet.CHANNEL_SETTING__MUTED_MEMBERS__TITLE}
          </DialogTitle>
        </DialogHeader>
        <div
          className="sendbird-more-members__popup-scroll max-h-[60vh] overflow-y-auto border-t"
          onScroll={onScroll}
        >
          {members.map((member, index) => (
            <RenderUserListItem
              key={member.userId}
              index={index}
              user={member}
              channel={channel}
              renderListItemMenu={(props) => (
                <UserListItemMenu
                  {...props}
                  renderMenuItems={() => (
                    <ChannelSettingsMenuItem
                      pending={isMutePending}
                      onClick={() => {
                        if (!isMutePending) unmuteAndRemove(member.userId);
                      }}
                    >
                      {translate('chat.unmute')}
                    </ChannelSettingsMenuItem>
                  )}
                />
              )}
            />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const MutedMembersList = ({
  renderUserListItem: RenderUserListItem = UserListItem,
}: MutedMembersListProps): ReactElement => {
  const [members, setMembers] = useState<Array<Member>>([]);
  const [hasNext, setHasNext] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const { stringSet } = useLocalization();
  const { channel } = useChannelSettingsContext();
  const translate = useTranslate();
  const { mutate: patchMuteUser, isPending: isMutePending } = useMuteUser();

  const refreshList = useCallback(() => {
    const memberUserListQuery = channel.createMemberListQuery({
      limit: 10,
      mutedMemberFilter: 'muted',
    } as any);

    void memberUserListQuery.next().then((nextMembers) => {
      setMembers(nextMembers);
      setHasNext(memberUserListQuery.hasNext);
    });
  }, [channel]);

  useEffect(refreshList, [channel.url, refreshList]);

  const unmuteAndRefresh = (userId: string) => {
    patchMuteUser(
      {
        channelUrl: channel.url,
        userId,
        muted: false,
      },
      { onSuccess: refreshList }
    );
  };

  return (
    <>
      {members.map((member, index) => (
        <RenderUserListItem
          key={member.userId}
          index={index}
          user={member}
          channel={channel}
          size="small"
          avatarSize="24px"
          renderListItemMenu={(props) => (
            <UserListItemMenu
              {...props}
              renderMenuItems={() => (
                <ChannelSettingsMenuItem
                  pending={isMutePending}
                  onClick={() => {
                    if (!isMutePending) unmuteAndRefresh(member.userId);
                  }}
                >
                  {translate('chat.unmute')}
                </ChannelSettingsMenuItem>
              )}
            />
          )}
        />
      ))}

      {members.length === 0 && (
        <p className="sendbird-channel-settings__empty-list">
          {stringSet.CHANNEL_SETTING__NO_UNMUTED}
        </p>
      )}

      {hasNext && (
        <div className="sendbird-channel-settings-accordion__footer">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowModal(true)}
          >
            {stringSet.CHANNEL_SETTING__MUTED_MEMBERS__TITLE_ALL}
          </Button>
        </div>
      )}

      {showModal && (
        <MutedMembersModal
          onCancel={() => {
            setShowModal(false);
            refreshList();
          }}
          renderUserListItem={RenderUserListItem}
        />
      )}
    </>
  );
};
